/**
 * Quiz configuration checks shared by the RescueX and facility course builders.
 *
 * The per-field rules in ContentQuiz sit inside a collapsed-by-default
 * Collapsible. React Hook Form drops the validation rules for a field once it
 * unmounts, so collapsing a content block let a stale "display 3 of 2
 * questions" save straight through. Students then hit a quiz that could never
 * be submitted or never be passed (RSCX-385).
 *
 * Checking at submit time instead of per field keeps the rule alive whether or
 * not the block is open. The backend enforces the same thing; this exists so
 * the author gets a readable message instead of the unhelpful string that
 * getErrorMessage produces for a deeply nested serializer error.
 */

/**
 * Find the first misconfigured quiz in a course's content blocks.
 *
 * @param {Array} contents - form values for `contents`
 * @returns {string|null} a message naming the offending block, or null if fine
 */
// eslint-disable-next-line import/prefer-default-export
export const getQuizConfigError = (contents) => {
  const blocks = contents ?? []

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index]
    const quiz = block?.quiz

    // No quiz, or a quiz with no questions yet, is a work in progress.
    if (quiz && quiz.questions?.length) {
      const total = quiz.questions.length
      const label = block.name || `Content block ${index + 1}`
      const display = Number(quiz.numQuestionsToDisplay)
      const toPass = Number(quiz.numQuestionsToPass)

      if (!(display >= 1 && display <= total)) {
        return `"${label}": the quiz shows ${display || 0} questions but only has ${total}. Set "Number of Questions to Display" between 1 and ${total}.`
      }
      if (!(toPass >= 1 && toPass <= total)) {
        return `"${label}": the quiz needs ${toPass || 0} correct answers but only has ${total} questions. Set "Number of Questions to Pass" between 1 and ${total}.`
      }
      if (toPass > display) {
        return `"${label}": the quiz needs ${toPass} correct answers but only shows ${display} questions. Lower "Number of Questions to Pass" to ${display} or less.`
      }
    }
  }

  return null
}
